/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  useEffect,
  useState
} from 'react';
import {MapTools} from '../types/types';

// defining useShowTool hook params...
interface ShowToolProps {
  ref: MutableRefObject<any>;
  initialState?: boolean;
  toolId: MapTools;
  activeTool: MapTools;
  setActiveTool: Function;
};

/**
 * Provides a hook for showing/hiding an ESRI custom widget to a map/view.
 *
 * ```typescript
 *
 *   const [show, toggleShow, handleClickOutside] = useShowTool({
 *     ref          : childRef,
 *     initialState : open,
 *     toolId       : MapTools.createGeoFence,
 *     activeTool,
 *     setActiveTool
 *    });
 *
 * ```
 *
 */
export const useShowTool = ({
  ref,
  toolId,
  activeTool,
  setActiveTool,
  initialState = false
}: ShowToolProps): [
    boolean,
    MouseEventHandler<Element>,
    (event: MouseEvent<Document>) => void,
    Function
  ] => {
  const [show, setShow] = useState(initialState);

  useEffect(() => {
    setShow(activeTool === toolId);
  }, [activeTool]);

  const toggleShow = (e) => {
    e?.stopPropagation?.();
    setActiveTool?.(show ? null : toolId);
    setShow(!show);
  };

  const handleClickOutside = (e) => {
    if (!ref?.current.contains(e.target)) {
      setShow(false);
    }
  };

  return [show, toggleShow, handleClickOutside, setShow];
};

export default useShowTool;
